import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, CreditCard, Users, Wifi, Check, MapPin, Gift, HelpCircle, Loader2, Info, ExternalLink, AlertCircle, Package } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getISPsForZip } from '../../utils/ispCoverage';

const OnboardingStep1 = ({ initialData, refData, onComplete, onSkip, saving }) => {
  // ============================================
  // BUNDLE STATE - NEW: Bundles come first!
  // ============================================
  // Initialize with any bundles already saved from initial onboarding
  const [selectedBundles, setSelectedBundles] = useState(initialData.bundles || []);
  const [loadingBundles, setLoadingBundles] = useState(true);
  const [availableBundles, setAvailableBundles] = useState([]);
  
  // Log what we received for debugging
  useEffect(() => {
    console.log('OnboardingStep1 received initialData.bundles:', initialData.bundles);
  }, [initialData.bundles]);

  // Services (individual, non-bundled)
  const [selectedServices, setSelectedServices] = useState(initialData.services || []);
  
  // Eligibility
  const [isStudent, setIsStudent] = useState(initialData.eligibility?.isStudent || false);
  const [isSenior, setIsSenior] = useState(initialData.eligibility?.isSenior || false);
  const [isVeteran, setIsVeteran] = useState(initialData.eligibility?.isVeteran || false);
  const [isFirstResponder, setIsFirstResponder] = useState(initialData.eligibility?.isFirstResponder || false);
  
  // Credit cards
  const [creditCards, setCreditCards] = useState(initialData.creditCards || []);
  const [showAddCard, setShowAddCard] = useState(false);
  const [newCardName, setNewCardName] = useState('');
  const [newCardIssuer, setNewCardIssuer] = useState('');
  const [newCardCredit, setNewCardCredit] = useState(0);
  const [isOtherCard, setIsOtherCard] = useState(false);
  
  // ============================================
  // MEMBERSHIPS - Pre-populate from bundles that are also memberships
  // ============================================
  const getMembershipsFromBundles = () => {
    const bundleMembershipMap = {
      'amazon_prime': { membership_name: 'Amazon Prime', membership_type: 'amazon_prime' },
      'walmart_plus': { membership_name: 'Walmart+', membership_type: 'walmart_plus' },
    };
    
    const existingMemberships = initialData.memberships || [];
    const membershipsFromBundles = [];
    
    (initialData.bundles || []).forEach(bundle => {
      const mapping = bundleMembershipMap[bundle.bundle_code];
      if (mapping) {
        const alreadyExists = existingMemberships.some(
          m => m.membership_type === mapping.membership_type
        );
        if (!alreadyExists) {
          membershipsFromBundles.push(mapping);
        }
      }
    });
    
    return [...existingMemberships, ...membershipsFromBundles];
  };

  const [memberships, setMemberships] = useState(getMembershipsFromBundles);
  const [showAddMembership, setShowAddMembership] = useState(false);
  const [newMembershipName, setNewMembershipName] = useState('');
  const [newMembershipType, setNewMembershipType] = useState('');
  const [isOtherMembership, setIsOtherMembership] = useState(false);
  
  // Location & ISP
  const [zipCode, setZipCode] = useState(initialData.zipCode || '');
  const [internetProvider, setInternetProvider] = useState(initialData.internetProvider || '');
  const [internetPlan, setInternetPlan] = useState(initialData.internetPlan || '');
  const [isOtherISP, setIsOtherISP] = useState(false);
  const [otherISPName, setOtherISPName] = useState('');
  
  // ISP data
  const [ispCoverage, setIspCoverage] = useState([]);
  const [ispPlans, setIspPlans] = useState([]);
  const [loadingISPs, setLoadingISPs] = useState(false);
  const [dataLastUpdated, setDataLastUpdated] = useState(null);
  const [showPlanHelp, setShowPlanHelp] = useState(false);

  // Section collapse state - Bundles expanded by default
  const [expandedSections, setExpandedSections] = useState({
    bundles: true,      // NEW: Start with bundles expanded
    services: false,    // Individual services collapsed initially
    eligibility: false,
    cards: false,
    memberships: false,
    location: false
  });

  // Bundle membership types for reference
  const bundleMembershipTypes = ['amazon_prime', 'walmart_plus'];

  // ============================================
  // SYNC MEMBERSHIPS WHEN BUNDLES CHANGE
  // ============================================
  useEffect(() => {
    const bundleMembershipMap = {
      'amazon_prime': { membership_name: 'Amazon Prime', membership_type: 'amazon_prime' },
      'walmart_plus': { membership_name: 'Walmart+', membership_type: 'walmart_plus' },
    };

    setMemberships(prev => {
      const updatedMemberships = [...prev];
      
      // Add memberships for newly selected bundles
      selectedBundles.forEach(bundle => {
        const mapping = bundleMembershipMap[bundle.bundle_code];
        if (mapping) {
          const alreadyExists = updatedMemberships.some(
            m => m.membership_type === mapping.membership_type
          );
          if (!alreadyExists) {
            updatedMemberships.push(mapping);
          }
        }
      });
      
      // Remove memberships for deselected bundles (only auto-added ones)
      const selectedBundleCodes = selectedBundles.map(b => b.bundle_code);
      return updatedMemberships.filter(m => {
        // Keep if it's not a bundle-mapped membership
        if (!bundleMembershipMap[m.membership_type]) return true;
        // Keep if the corresponding bundle is still selected
        const bundleCode = Object.keys(bundleMembershipMap).find(
          code => bundleMembershipMap[code].membership_type === m.membership_type
        );
        return selectedBundleCodes.includes(bundleCode);
      });
    });
  }, [selectedBundles]);

  // ============================================
  // LOAD BUNDLES FROM DATABASE
  // ============================================
  useEffect(() => {
    loadBundles();
  }, []);

  const loadBundles = async () => {
    setLoadingBundles(true);
    try {
      const { data: bundles, error } = await supabase
        .from('bundles')
        .select('*, bundle_services(service_name, included_tier)')
        .eq('is_active', true)
        .order('monthly_cost');

      if (error) throw error;

      if (bundles && bundles.length > 0) {
        setAvailableBundles(bundles);
      } else {
        // Fallback bundles if database is empty
        setAvailableBundles(getFallbackBundles());
      }
    } catch (error) {
      console.error('Error loading bundles:', error);
      setAvailableBundles(getFallbackBundles());
    } finally {
      setLoadingBundles(false);
    }
  };

  const getFallbackBundles = () => [
    {
      id: 'disney_duo_basic',
      bundle_name: 'Disney Bundle Duo Basic',
      bundle_code: 'disney_duo_basic',
      monthly_cost: 10.99,
      notes: 'Disney+ and Hulu with ads',
      bundle_services: [
        { service_name: 'Disney+', included_tier: 'Basic (with ads)' },
        { service_name: 'Hulu', included_tier: 'With Ads' }
      ]
    },
    {
      id: 'disney_duo_premium',
      bundle_name: 'Disney Bundle Duo Premium',
      bundle_code: 'disney_duo_premium',
      monthly_cost: 19.99,
      notes: 'Disney+ and Hulu without ads',
      bundle_services: [
        { service_name: 'Disney+', included_tier: 'Premium (no ads)' },
        { service_name: 'Hulu', included_tier: 'No Ads' }
      ]
    },
    {
      id: 'disney_trio_basic',
      bundle_name: 'Disney Bundle Trio Basic',
      bundle_code: 'disney_trio_basic',
      monthly_cost: 16.99,
      notes: 'Disney+, Hulu, and ESPN+ with ads',
      bundle_services: [
        { service_name: 'Disney+', included_tier: 'Basic (with ads)' },
        { service_name: 'Hulu', included_tier: 'With Ads' },
        { service_name: 'ESPN+', included_tier: 'Standard' }
      ]
    },
    {
      id: 'disney_hulu_max_ads',
      bundle_name: 'Disney+, Hulu, Max Bundle (Ads)',
      bundle_code: 'disney_hulu_max_ads',
      monthly_cost: 16.99,
      notes: 'All three services with ads',
      bundle_services: [
        { service_name: 'Disney+', included_tier: 'Basic (with ads)' },
        { service_name: 'Hulu', included_tier: 'With Ads' },
        { service_name: 'Max', included_tier: 'With Ads' }
      ]
    },
    {
      id: 'apple_one_individual',
      bundle_name: 'Apple One Individual',
      bundle_code: 'apple_one_individual',
      monthly_cost: 19.95,
      notes: 'Apple TV+, Apple Music, Apple Arcade, 50GB iCloud+',
      bundle_services: [
        { service_name: 'Apple TV+', included_tier: 'Standard' },
        { service_name: 'Apple Music', included_tier: 'Individual' }
      ]
    },
    {
      id: 'amazon_prime',
      bundle_name: 'Amazon Prime',
      bundle_code: 'amazon_prime',
      monthly_cost: 14.99,
      notes: 'Prime Video, free shipping, Prime Music, Prime Gaming',
      bundle_services: [
        { service_name: 'Amazon Prime Video', included_tier: 'Standard' }
      ]
    },
    {
      id: 'walmart_plus',
      bundle_name: 'Walmart+',
      bundle_code: 'walmart_plus',
      monthly_cost: 12.95,
      notes: 'Free delivery, fuel discounts, Paramount+ Essential included',
      bundle_services: [
        { service_name: 'Paramount+', included_tier: 'Essential (with ads)' }
      ]
    }
  ];

  // ============================================
  // COMPUTE BUNDLED SERVICES
  // ============================================
  const bundledServiceNames = useMemo(() => {
    const services = new Set();
    selectedBundles.forEach(bundle => {
      const bundleData = availableBundles.find(b => b.bundle_code === bundle.bundle_code);
      bundleData?.bundle_services?.forEach(bs => {
        services.add(bs.service_name);
      });
    });
    return services;
  }, [selectedBundles, availableBundles]);

  // ============================================
  // LOAD ISP DATA BASED ON ZIP CODE
  // ============================================
  useEffect(() => {
    if (zipCode.length === 5) {
      loadISPCoverage(zipCode);
    } else {
      setIspCoverage([]);
      setInternetProvider('');
      setInternetPlan('');
      setIsOtherISP(false);
    }
  }, [zipCode]);

  useEffect(() => {
    if (internetProvider && !isOtherISP && internetProvider !== 'none' && internetProvider !== 'not_listed') {
      loadISPPlans(internetProvider);
    } else {
      setIspPlans([]);
      setInternetPlan('');
    }
  }, [internetProvider, isOtherISP]);

  const loadISPCoverage = async (zip) => {
    setLoadingISPs(true);
    try {
      const tieredISPs = getISPsForZip(zip);
      
      const flatList = [
        ...tieredISPs.regional.filter(i => i.type === 'fiber').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'fiber',
          coverage_quality: i.quality,
          check_url: i.checkUrl
        })),
        ...tieredISPs.metro.filter(i => i.type === 'fiber').map(i => ({
          isp_name: `${i.name} (${i.metro})`,
          isp_code: i.code,
          coverage_type: 'fiber',
          coverage_quality: i.quality,
          metro: i.metro,
          check_url: i.checkUrl
        })),
        ...tieredISPs.regional.filter(i => i.type === 'cable').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'cable',
          coverage_quality: i.quality,
          check_url: i.checkUrl
        })),
        ...tieredISPs.national.filter(i => i.type === 'fixed_wireless').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: '5G wireless',
          coverage_quality: i.quality,
          note: i.note,
          check_url: i.checkUrl
        })),
        ...tieredISPs.national.filter(i => i.type === 'satellite').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'satellite',
          coverage_quality: i.quality,
          note: i.note,
          check_url: i.checkUrl
        }))
      ];

      if (flatList.length > 0) {
        setIspCoverage(flatList);
      } else {
        setIspCoverage([]);
      }
      
      const { data: updateInfo } = await supabase
        .from('reference_data_updates')
        .select('updated_at')
        .eq('table_name', 'isp_service_areas')
        .single();
      setDataLastUpdated(updateInfo?.updated_at);
    } catch (error) {
      console.error('Error loading ISP coverage:', error);
      setIspCoverage([]);
    } finally {
      setLoadingISPs(false);
    }
  };

  const loadISPPlans = async (ispName) => {
    try {
      const isp = ispCoverage.find(i => i.isp_name === ispName);
      if (!isp?.isp_code) return;

      const { data: plans, error } = await supabase
        .from('isp_plans')
        .select('*')
        .eq('isp_code', isp.isp_code)
        .eq('is_current', true)
        .order('sort_order');

      if (error) throw error;
      setIspPlans(plans || []);
    } catch (error) {
      console.error('Error loading ISP plans:', error);
      setIspPlans([]);
    }
  };

  // ============================================
  // DERIVE LISTS FROM REFERENCE DATA
  // ============================================

  // Get streaming services, EXCLUDING those already covered by selected bundles
  const streamingServices = useMemo(() => {
    const allServices = refData.streamingServices?.length > 0 
      ? refData.streamingServices.map(s => s.service_name)
      : [
          'Netflix', 'Disney+', 'Hulu', 'Amazon Prime Video', 'Apple TV+',
          'Max', 'Paramount+', 'Peacock', 'Discovery+', 'ESPN+',
          'YouTube TV', 'Sling TV', 'FuboTV', 'Showtime', 'Starz',
          'AMC+', 'BET+', 'Crunchyroll', 'Shudder'
        ];
    
    // Filter out services that are already in selected bundles
    return allServices.filter(service => !bundledServiceNames.has(service));
  }, [refData.streamingServices, bundledServiceNames]);

  // Get ISPs
  const getInternetProviders = () => {
    if (ispCoverage.length > 0) {
      return ispCoverage;
    }

    const ispSet = new Set();
    refData.ispPerks?.forEach(perk => {
      if (perk.isp_name) ispSet.add(perk.isp_name);
    });
    
    if (ispSet.size > 0) {
      return Array.from(ispSet).sort().map(name => ({
        isp_name: name,
        isp_code: name.toLowerCase().replace(/\s+/g, '_')
      }));
    }
    
    return [
      { isp_name: 'Xfinity', isp_code: 'xfinity' },
      { isp_name: 'Spectrum', isp_code: 'spectrum' },
      { isp_name: 'AT&T Fiber', isp_code: 'att_fiber' },
      { isp_name: 'Verizon Fios', isp_code: 'verizon_fios' },
      { isp_name: 'CenturyLink', isp_code: 'centurylink' },
      { isp_name: 'Cox', isp_code: 'cox' },
      { isp_name: 'T-Mobile 5G Home Internet', isp_code: 'tmobile_home' },
      { isp_name: 'Starlink', isp_code: 'starlink' },
    ];
  };

  const internetProviders = getInternetProviders();

  // Get ISP perks for the selected provider
  const selectedISPPerks = useMemo(() => {
    const currentISP = isOtherISP ? otherISPName : internetProvider;
    if (!currentISP || currentISP === 'none' || currentISP === 'not_listed') return [];
    
    return refData.ispPerks?.filter(perk => 
      perk.isp_name?.toLowerCase() === currentISP?.toLowerCase() &&
      perk.service_name &&
      perk.service_name.toLowerCase() !== 'none'
    ) || [];
  }, [internetProvider, isOtherISP, otherISPName, refData.ispPerks]);

  // Get card options
  const getCardOptions = () => {
    const cardMap = new Map();
    
    refData.cardPerks?.forEach(perk => {
      if (!perk.card_name) return;
      
      if (!cardMap.has(perk.card_name)) {
        cardMap.set(perk.card_name, {
          name: perk.card_name,
          issuer: perk.issuer || '',
          hasStreamingCredit: false,
          creditAmount: 0,
          notes: []
        });
      }
      
      const card = cardMap.get(perk.card_name);
      
      if (perk.perk_type === 'streaming_credit' && perk.credit_amount) {
        card.hasStreamingCredit = true;
        card.creditAmount = Math.max(card.creditAmount, perk.credit_amount);
      }
      
      if (perk.perk_type === 'included_service' && perk.service_name) {
        card.notes.push(`Includes ${perk.service_name}`);
      }
      
      if (perk.perk_type === 'included_membership' && perk.included_membership) {
        card.notes.push(`Includes ${perk.included_membership}`);
      }
    });
    
    if (cardMap.size > 0) {
      const cards = Array.from(cardMap.values()).sort((a, b) => {
        if (b.creditAmount !== a.creditAmount) return b.creditAmount - a.creditAmount;
        return a.name.localeCompare(b.name);
      });
      return [...cards, { name: 'Other', issuer: '', hasStreamingCredit: false, creditAmount: 0, notes: [] }];
    }
    
    return [
      { name: 'American Express Platinum', issuer: 'American Express', hasStreamingCredit: true, creditAmount: 25, notes: ['Includes Walmart+'] },
      { name: 'American Express Gold', issuer: 'American Express', hasStreamingCredit: true, creditAmount: 10, notes: [] },
      { name: 'Blue Cash Preferred', issuer: 'American Express', hasStreamingCredit: true, creditAmount: 10, notes: [] },
      { name: 'Blue Cash Everyday', issuer: 'American Express', hasStreamingCredit: true, creditAmount: 7, notes: [] },
      { name: 'Chase Sapphire Reserve', issuer: 'Chase', hasStreamingCredit: false, creditAmount: 0, notes: ['Includes Apple TV+ & Apple Music'] },
      { name: 'Other', issuer: '', hasStreamingCredit: false, creditAmount: 0, notes: [] }
    ];
  };

  const cardOptions = getCardOptions();

  // Get membership options
  const getMembershipOptions = () => {
    const membershipMap = new Map();
    
    refData.membershipPerks?.forEach(perk => {
      if (!perk.membership_type) return;
      
      if (!membershipMap.has(perk.membership_type)) {
        membershipMap.set(perk.membership_type, {
          name: perk.membership_name || perk.membership_type,
          type: perk.membership_type,
          perks: [],
          choiceGroups: new Map()
        });
      }
      
      const membership = membershipMap.get(perk.membership_type);
      
      if (perk.service_name) {
        if (perk.is_choice && perk.choice_group) {
          if (!membership.choiceGroups.has(perk.choice_group)) {
            membership.choiceGroups.set(perk.choice_group, []);
          }
          membership.choiceGroups.get(perk.choice_group).push(perk.service_name);
        } else {
          const perkDesc = `Includes ${perk.service_name}`;
          if (!membership.perks.includes(perkDesc)) {
            membership.perks.push(perkDesc);
          }
        }
      }
    });
    
    membershipMap.forEach(membership => {
      membership.choiceGroups.forEach((services, groupName) => {
        if (services.length > 1) {
          membership.perks.unshift(`Choice of ${services.join(' or ')}`);
        } else if (services.length === 1) {
          membership.perks.unshift(`Choice of ${services[0]}`);
        }
      });
      delete membership.choiceGroups;
    });
    
    if (membershipMap.size > 0) {
      const memberships = Array.from(membershipMap.values()).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      return [...memberships, { name: 'Other', type: 'other', perks: [] }];
    }
    
    return [
      { name: 'Amazon Prime', type: 'amazon_prime', perks: ['Includes Prime Video'] },
      { name: 'Walmart+', type: 'walmart_plus', perks: ['Choice of Paramount+ or Peacock'] },
      { name: 'Instacart+', type: 'instacart_plus', perks: ['Includes Peacock Premium'] },
      { name: 'DoorDash DashPass', type: 'dashpass', perks: ['Includes Max with Ads'] },
      { name: 'Other', type: 'other', perks: [] }
    ];
  };

  const membershipOptions = getMembershipOptions();

  // ============================================
  // HANDLERS
  // ============================================

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Bundle selection handler
  const toggleBundle = (bundle) => {
    setSelectedBundles(prev => {
      const isSelected = prev.some(b => b.bundle_code === bundle.bundle_code);
      if (isSelected) {
        return prev.filter(b => b.bundle_code !== bundle.bundle_code);
      } else {
        return [...prev, {
          bundle_code: bundle.bundle_code,
          bundle_name: bundle.bundle_name,
          monthly_cost: bundle.monthly_cost
        }];
      }
    });
    
    // Also remove any individually selected services that are now in a bundle
    const bundleServiceNames = bundle.bundle_services?.map(bs => bs.service_name) || [];
    setSelectedServices(prev => prev.filter(s => !bundleServiceNames.includes(s)));
  };

  const toggleService = (service) => {
    setSelectedServices(prev =>
      prev.includes(service)
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const addCard = () => {
    if (!newCardName && !isOtherCard) return;
    
    const cardPerk = refData.cardPerks?.find(c => 
      c.card_name === newCardName && c.perk_type === 'streaming_credit'
    );
    
    const fallbackCard = cardOptions.find(c => c.name === newCardName);
    
    const newCard = {
      card_name: isOtherCard ? newCardName : (newCardName || ''),
      issuer: isOtherCard ? newCardIssuer : (cardPerk?.issuer || fallbackCard?.issuer || ''),
      streaming_credit_monthly: isOtherCard 
        ? newCardCredit 
        : (parseFloat(cardPerk?.monthly_credit_amount) || fallbackCard?.creditAmount || 0)
    };
    
    setCreditCards(prev => [...prev, newCard]);
    setNewCardName('');
    setNewCardIssuer('');
    setNewCardCredit(0);
    setIsOtherCard(false);
    setShowAddCard(false);
  };

  const removeCard = (index) => {
    setCreditCards(prev => prev.filter((_, i) => i !== index));
  };

  const addMembership = () => {
    if (!newMembershipType && !isOtherMembership) return;
    
    const membershipInfo = membershipOptions.find(m => m.type === newMembershipType);
    const newMembership = {
      membership_name: isOtherMembership ? newMembershipName : (membershipInfo?.name || newMembershipName),
      membership_type: isOtherMembership ? 'other' : newMembershipType
    };
    
    setMemberships(prev => [...prev, newMembership]);
    setNewMembershipName('');
    setNewMembershipType('');
    setIsOtherMembership(false);
    setShowAddMembership(false);
  };

  const removeMembership = (index) => {
    setMemberships(prev => prev.filter((_, i) => i !== index));
  };

  const handleISPSelect = (ispName) => {
    if (ispName === 'none') {
      setInternetProvider('none');
      setIsOtherISP(false);
      setOtherISPName('');
    } else if (ispName === 'not_listed') {
      setInternetProvider('not_listed');
      setIsOtherISP(true);
      setOtherISPName('');
    } else {
      setInternetProvider(ispName);
      setIsOtherISP(false);
      setOtherISPName('');
    }
    setInternetPlan('');
  };

  const handleContinue = () => {
    // Combine bundled services with individually selected services
    const allServices = [
      ...Array.from(bundledServiceNames),
      ...selectedServices
    ];

    const step1Data = {
      services: allServices,
      bundles: selectedBundles,
      eligibility: {
        isStudent,
        isSenior,
        isVeteran,
        isFirstResponder
      },
      creditCards,
      memberships,
      zipCode,
      internetProvider: isOtherISP ? otherISPName : internetProvider,
      internetPlan
    };
    
    onComplete(step1Data);
  };

  // Get relevant perks
  const getRelevantPerks = () => {
    const perks = [];
    const currentISP = isOtherISP ? otherISPName : internetProvider;
    
    if (currentISP && currentISP !== 'none' && currentISP !== 'not_listed') {
      const ispPerks = refData.ispPerks?.filter(p => 
        p.isp_name?.toLowerCase() === currentISP?.toLowerCase() && 
        p.service_name && 
        p.service_name.toLowerCase() !== 'none'
      ) || [];
      perks.push(...ispPerks.map(p => ({
        type: 'isp',
        source: currentISP,
        service: p.service_name,
        tier: p.included_tier,
        note: p.notes
      })));
    }
    
    memberships.forEach(m => {
      const membershipPerks = refData.membershipPerks?.filter(p => p.membership_type === m.membership_type) || [];
      perks.push(...membershipPerks.map(p => ({
        type: 'membership',
        source: m.membership_name,
        service: p.service_name,
        tier: p.included_tier,
        isChoice: p.is_choice,
        note: p.notes
      })));
    });
    
    return perks;
  };

  const relevantPerks = getRelevantPerks();

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Helper to check if membership came from a bundle
  const isMembershipFromBundle = (membershipType) => {
    return bundleMembershipTypes.includes(membershipType) && 
           selectedBundles.some(b => b.bundle_code === membershipType);
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const SectionHeader = ({ title, icon: Icon, section, badge }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-3">
        {typeof Icon === 'string' ? (
          <span className="w-5 h-5 text-purple-600">{Icon}</span>
        ) : Icon ? (
          <Icon className="w-5 h-5 text-purple-600" />
        ) : null}
        <span className="font-semibold text-gray-900">{title}</span>
        {badge && (
          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
            {badge}
          </span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-gray-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  // Calculate total services selected
  const totalServicesSelected = bundledServiceNames.size + selectedServices.length;

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Tell us about your setup</h2>
        <p className="text-gray-600 mt-1">We'll use this to find savings and perks you might be missing</p>
      </div>

      {/* ========================================
          SECTION 1: BUNDLES (NEW - First!)
          ======================================== */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <SectionHeader 
          title="Streaming Bundles" 
          icon={Package}
          section="bundles"
          badge={selectedBundles.length > 0 ? `${selectedBundles.length} selected` : null}
        />
        
        {expandedSections.bundles && (
          <div className="p-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">
              Do you subscribe to any bundled streaming packages? Bundles often save money!
            </p>
            
            {loadingBundles ? (
              <div className="flex items-center justify-center py-6 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading bundles...
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {availableBundles.map(bundle => {
                  const isSelected = selectedBundles.some(b => b.bundle_code === bundle.bundle_code);
                  const includedServices = bundle.bundle_services?.map(bs => bs.service_name) || [];
                  const isMembershipBundle = bundleMembershipTypes.includes(bundle.bundle_code);
                  
                  return (
                    <label
                      key={bundle.bundle_code}
                      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-purple-100 border-2 border-purple-500 ring-2 ring-purple-200'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:border-purple-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleBundle(bundle)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isSelected
                          ? 'bg-purple-600 text-white'
                          : 'border-2 border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-gray-900">{bundle.bundle_name}</span>
                          <span className="text-purple-600 font-semibold whitespace-nowrap">
                            ${bundle.monthly_cost}/mo
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {bundle.notes || `Includes: ${includedServices.join(', ')}`}
                        </p>
                        {isSelected && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {includedServices.map(service => (
                              <span 
                                key={service}
                                className="px-2 py-0.5 bg-purple-200 text-purple-800 text-xs rounded-full"
                              >
                                ✓ {service}
                              </span>
                            ))}
                            {isMembershipBundle && (
                              <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs rounded-full">
                                ✓ Membership auto-added
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            
            {selectedBundles.length > 0 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800">
                  <Check className="w-4 h-4" />
                  <span className="font-medium">
                    {bundledServiceNames.size} services covered by your bundles
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ========================================
          SECTION 2: INDIVIDUAL STREAMING SERVICES
          ======================================== */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <SectionHeader 
          title="Individual Streaming Services" 
          icon="📺"
          section="services"
          badge={selectedServices.length > 0 ? `${selectedServices.length} selected` : null}
        />
        
        {expandedSections.services && (
          <div className="p-4 border-t border-gray-200">
            {bundledServiceNames.size > 0 && (
              <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
                <Info className="w-4 h-4 inline mr-1" />
                Services from your bundles are already included above. Select any <strong>additional</strong> services you pay for separately:
              </div>
            )}
            
            <p className="text-sm text-gray-600 mb-3">
              {bundledServiceNames.size > 0 
                ? "Select any other streaming services you pay for individually:"
                : "Select all the streaming services you currently use:"
              }
            </p>
            
            {streamingServices.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p>All available services are covered by your bundles!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                {streamingServices.map(service => (
                  <label
                    key={service}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedServices.includes(service)
                        ? 'bg-purple-100 border-2 border-purple-500'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedServices.includes(service)}
                      onChange={() => toggleService(service)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded flex items-center justify-center ${
                      selectedServices.includes(service)
                        ? 'bg-purple-600 text-white'
                        : 'border-2 border-gray-300'
                    }`}>
                      {selectedServices.includes(service) && <Check className="w-3 h-3" />}
                    </div>
                    <span className="text-sm text-gray-900">{service}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ========================================
          SECTION 3: LOCATION & INTERNET
          ======================================== */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <SectionHeader 
          title="Location & Internet" 
          icon={Wifi}
          section="location"
          badge={(internetProvider && internetProvider !== 'none') || isOtherISP ? '✓' : null}
        />
        
        {expandedSections.location && (
          <div className="p-4 border-t border-gray-200 space-y-4">
            {/* Zip Code Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Zip Code
              </label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="Enter 5-digit zip code"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                maxLength="5"
              />
              <p className="text-xs text-gray-500 mt-1">
                We use this to show internet providers available in your area
              </p>
            </div>
            
            {/* ISP Selection */}
            {zipCode.length === 5 && (
              <div className="space-y-3">
                {loadingISPs ? (
                  <div className="flex items-center justify-center py-6 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Finding providers in your area...
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        Select Your Internet Provider
                      </label>
                    </div>

                    {ispCoverage.length > 0 && (
                      <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                        <Info className="w-4 h-4 inline mr-1 text-blue-600" />
                        Based on your location ({zipCode}), here are the internet providers in your area:
                      </p>
                    )}

                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {internetProviders.map((isp) => {
                        const ispName = isp.isp_name || isp;
                        const ispCode = isp.isp_code || ispName.toLowerCase().replace(/\s+/g, '_');
                        const perks = refData.ispPerks?.filter(p => 
                          p.isp_name?.toLowerCase() === ispName?.toLowerCase() &&
                          p.service_name &&
                          p.service_name.toLowerCase() !== 'none'
                        ) || [];
                        const hasPerks = perks.length > 0;
                        
                        return (
                          <label
                            key={ispCode}
                            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                              internetProvider === ispName
                                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="isp"
                              checked={internetProvider === ispName}
                              onChange={() => handleISPSelect(ispName)}
                              className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-900">{ispName}</span>
                                {hasPerks && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                                    <Gift className="w-3 h-3" />
                                    Streaming perks
                                  </span>
                                )}
                              </div>
                              {isp.coverage_type && (
                                <p className="text-sm text-gray-500 mt-0.5 capitalize">
                                  {isp.coverage_type}
                                </p>
                              )}
                              {hasPerks && internetProvider === ispName && (
                                <p className="mt-1 text-sm text-green-700">
                                  Includes: {perks.map(p => p.service_name).join(', ')}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}

                      {/* No home internet */}
                      <label
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                          internetProvider === 'none'
                            ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="isp"
                          checked={internetProvider === 'none'}
                          onChange={() => handleISPSelect('none')}
                          className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">I don't have home internet</span>
                          <p className="text-sm text-gray-500">Mobile data only</p>
                        </div>
                      </label>

                      {/* Not listed */}
                      <label
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                          internetProvider === 'not_listed'
                            ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="isp"
                          checked={internetProvider === 'not_listed'}
                          onChange={() => handleISPSelect('not_listed')}
                          className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">My provider isn't listed</span>
                          <p className="text-sm text-gray-500">You can tell us more later</p>
                        </div>
                      </label>
                    </div>

                    {isOtherISP && (
                      <input
                        type="text"
                        value={otherISPName}
                        onChange={(e) => setOtherISPName(e.target.value)}
                        placeholder="Enter your internet provider name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    )}
                  </>
                )}
              </div>
            )}

            {/* ISP Perks Preview */}
            {selectedISPPerks.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-900">Streaming perks from your ISP</span>
                </div>
                <div className="space-y-1">
                  {selectedISPPerks.map((perk, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-green-800">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>
                        {perk.service_name}
                        {perk.included_tier && <span className="text-green-600"> ({perk.included_tier})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ========================================
          SECTION 4: ELIGIBILITY
          ======================================== */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <SectionHeader 
          title="Eligibility & Discounts" 
          icon="✅"
          section="eligibility"
          badge={[isStudent, isSenior, isVeteran, isFirstResponder].filter(Boolean).length > 0 
            ? `${[isStudent, isSenior, isVeteran, isFirstResponder].filter(Boolean).length} selected` 
            : null}
        />
        
        {expandedSections.eligibility && (
          <div className="p-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">Many services offer special pricing. Check all that apply:</p>
            <div className="space-y-2">
              {[
                { label: 'Student', value: isStudent, setter: setIsStudent },
                { label: 'Senior (55+)', value: isSenior, setter: setIsSenior },
                { label: 'Veteran or Active Military', value: isVeteran, setter: setIsVeteran },
                { label: 'First Responder', value: isFirstResponder, setter: setIsFirstResponder }
              ].map(item => (
                <label
                  key={item.label}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    item.value
                      ? 'bg-purple-100 border-2 border-purple-500'
                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={item.value}
                    onChange={(e) => item.setter(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${
                    item.value
                      ? 'bg-purple-600 text-white'
                      : 'border-2 border-gray-300'
                  }`}>
                    {item.value && <Check className="w-3 h-3" />}
                  </div>
                  <span className="text-gray-900">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ========================================
          SECTION 5: CREDIT CARDS
          ======================================== */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <SectionHeader 
          title="Premium Credit Cards" 
          icon={CreditCard}
          section="cards"
          badge={creditCards.length > 0 ? `${creditCards.length} added` : null}
        />
        
        {expandedSections.cards && (
          <div className="p-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">
              Many premium cards offer streaming credits or perks. Add yours to maximize savings:
            </p>
            
            {creditCards.length > 0 && (
              <div className="space-y-2 mb-4">
                {creditCards.map((card, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">{card.card_name}</div>
                      {card.streaming_credit_monthly > 0 && (
                        <div className="text-sm text-green-600">
                          ${card.streaming_credit_monthly}/mo streaming credit
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeCard(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {showAddCard ? (
              <div className="border-2 border-purple-200 rounded-lg p-4 space-y-3">
                <select
                  value={isOtherCard ? 'Other' : newCardName}
                  onChange={(e) => {
                    if (e.target.value === 'Other') {
                      setIsOtherCard(true);
                      setNewCardName('');
                      setNewCardIssuer('');
                      setNewCardCredit(0);
                    } else {
                      setIsOtherCard(false);
                      setNewCardName(e.target.value);
                      const card = cardOptions.find(c => c.name === e.target.value);
                      setNewCardIssuer(card?.issuer || '');
                      setNewCardCredit(card?.creditAmount || 0);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select a card...</option>
                  {cardOptions.map(card => (
                    <option key={card.name} value={card.name}>
                      {card.name}
                      {card.hasStreamingCredit && ` ($${card.creditAmount}/mo credit)`}
                      {card.notes.length > 0 && ` - ${card.notes[0]}`}
                    </option>
                  ))}
                </select>
                
                {isOtherCard && (
                  <>
                    <input
                      type="text"
                      placeholder="Enter card name"
                      value={newCardName}
                      onChange={(e) => setNewCardName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <input
                      type="text"
                      placeholder="Card issuer (e.g., Chase, Amex)"
                      value={newCardIssuer}
                      onChange={(e) => setNewCardIssuer(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">$</span>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Monthly streaming credit (if any)"
                        value={newCardCredit || ''}
                        onChange={(e) => setNewCardCredit(parseFloat(e.target.value) || 0)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-500">/mo</span>
                    </div>
                  </>
                )}
                
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowAddCard(false);
                      setIsOtherCard(false);
                      setNewCardName('');
                      setNewCardIssuer('');
                      setNewCardCredit(0);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addCard}
                    disabled={!newCardName}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
                  >
                    Add Card
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddCard(true)}
                className="w-full px-4 py-2 border-2 border-dashed border-purple-300 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                + Add Credit Card
              </button>
            )}
          </div>
        )}
      </div>

      {/* ========================================
          SECTION 6: MEMBERSHIPS
          ======================================== */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <SectionHeader 
          title="Memberships" 
          icon={Users}
          section="memberships"
          badge={memberships.length > 0 ? `${memberships.length} added` : null}
        />
        
        {expandedSections.memberships && (
          <div className="p-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">
              Some memberships include free streaming. Add yours:
            </p>
            
            {memberships.length > 0 && (
              <div className="space-y-2 mb-4">
                {memberships.map((membership, index) => {
                  const info = membershipOptions.find(m => m.type === membership.membership_type);
                  const isFromBundle = isMembershipFromBundle(membership.membership_type);
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{membership.membership_name}</span>
                          {isFromBundle && (
                            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                              From bundle
                            </span>
                          )}
                        </div>
                        {info?.perks.length > 0 && (
                          <div className="text-sm text-green-600">{info.perks[0]}</div>
                        )}
                      </div>
                      <button
                        onClick={() => removeMembership(index)}
                        className="text-red-600 hover:text-red-700 text-sm"
                        disabled={isFromBundle}
                        title={isFromBundle ? "Remove the bundle to remove this membership" : "Remove membership"}
                      >
                        {isFromBundle ? (
                          <span className="text-gray-400 cursor-not-allowed">Linked</span>
                        ) : (
                          'Remove'
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {showAddMembership ? (
              <div className="border-2 border-purple-200 rounded-lg p-4 space-y-3">
                <select
                  value={isOtherMembership ? 'other' : newMembershipType}
                  onChange={(e) => {
                    if (e.target.value === 'other') {
                      setIsOtherMembership(true);
                      setNewMembershipType('other');
                      setNewMembershipName('');
                    } else {
                      setIsOtherMembership(false);
                      setNewMembershipType(e.target.value);
                      const membership = membershipOptions.find(m => m.type === e.target.value);
                      setNewMembershipName(membership?.name || '');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select a membership...</option>
                  {membershipOptions
                    .filter(m => !memberships.some(existing => existing.membership_type === m.type))
                    .map(membership => (
                      <option key={membership.type} value={membership.type}>
                        {membership.name}
                        {membership.perks.length > 0 && ` - ${membership.perks[0]}`}
                      </option>
                    ))}
                </select>
                
                {isOtherMembership && (
                  <input
                    type="text"
                    placeholder="Enter membership name"
                    value={newMembershipName}
                    onChange={(e) => setNewMembershipName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                )}
                
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowAddMembership(false);
                      setIsOtherMembership(false);
                      setNewMembershipName('');
                      setNewMembershipType('');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addMembership}
                    disabled={!newMembershipType && !newMembershipName}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
                  >
                    Add Membership
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddMembership(true)}
                className="w-full px-4 py-2 border-2 border-dashed border-purple-300 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                + Add Membership
              </button>
            )}
          </div>
        )}
      </div>

      {/* Perks Preview */}
      {relevantPerks.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-semibold text-green-800 mb-2">🎉 Perks we found!</h3>
          <ul className="space-y-1">
            {relevantPerks.slice(0, 3).map((perk, index) => (
              <li key={index} className="text-sm text-green-700">
                • {perk.service} {perk.tier && `(${perk.tier})`} — included with {perk.source}
              </li>
            ))}
            {relevantPerks.length > 3 && (
              <li className="text-sm text-green-600 font-medium">
                + {relevantPerks.length - 3} more perks to configure in the next step
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Continue Button */}
      <div className="sticky bottom-0 bg-white pt-4 pb-2 -mx-6 px-6 sm:mx-0 sm:px-0 sm:static border-t border-gray-200 sm:border-0">
        <button
          onClick={handleContinue}
          disabled={saving || totalServicesSelected === 0}
          className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-semibold py-3 rounded-lg transition-colors"
        >
          {saving ? 'Saving...' : 'Continue to Payment Details'}
        </button>
        {totalServicesSelected === 0 && (
          <p className="text-center text-sm text-gray-500 mt-2">
            Select at least one bundle or streaming service to continue
          </p>
        )}
        {totalServicesSelected > 0 && (
          <p className="text-center text-sm text-green-600 mt-2">
            {totalServicesSelected} service{totalServicesSelected !== 1 ? 's' : ''} selected
            {selectedBundles.length > 0 && ` (${selectedBundles.length} bundle${selectedBundles.length !== 1 ? 's' : ''})`}
          </p>
        )}
      </div>
    </div>
  );
};

export default OnboardingStep1;